// src/pages/BookService.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ServiceRequestForm from "../components/ServiceRequestForm";
import Header from "../components/header";
import Footer from "../components/footer";
import { Box, Typography } from "@mui/material";
import getApiBase from "../components/getApiBase";

const BookService = () => {
  const navigate = useNavigate();

  const [cars, setCars] = useState([]);
  const [services, setServices] = useState([]);
  const [autoshops, setAutoshops] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedAutoshop, setSelectedAutoshop] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [serviceDescription, setServiceDescription] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [userZip, setUserZip] = useState("");
  const [radius, setRadius] = useState(10);
  const [loadingShops, setLoadingShops] = useState(false);
  
  // NEW: Request type selection
  const [requestType, setRequestType] = useState(""); // 'direct', 'announcement', or 'bidding'
  const [availableRequestTypes, setAvailableRequestTypes] = useState({
    direct: true,
    announcement: false,
    bidding: false
  });
  const [requestTypesLoaded, setRequestTypesLoaded] = useState(false);
  const [isSubmittingServiceRequest, setIsSubmittingServiceRequest] = useState(false);
  const [dropoffWeekStart, setDropoffWeekStart] = useState(null); // Monday of week (YYYY-MM-DD)
  const [dropoffAvailableDays, setDropoffAvailableDays] = useState([]); // e.g. ['Mon','Tue','Wed','Thu'] for that week

  const today = new Date();

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // zero-padded
  const day = String(today.getDate()).padStart(2, "0"); // zero-padded
  const requestDate = `${year}-${month}-${day}`;

  // Don't auto-fetch geolocation - wait for user to enter ZIP code
  // This ensures we use the form ZIP code, not the customer's address location

  // Fetch user profile to get zip code and initialize userZip, then auto-load shops
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/user/profile`, {
          credentials: "include",
        });
        const data = await res.json();
        const userZipCode = data.address?.zip_code || "";
        setZipCode(userZipCode);
        // Initialize userZip with user's ZIP code
        setUserZip(userZipCode);
        
        // Auto-load shops on page load using user's address ZIP code
        if (userZipCode && userZipCode.length >= 5) {
          console.log(`📍 Auto-loading shops for user's address ZIP: ${userZipCode}`);
          // Get coordinates from user's ZIP and load shops
          try {
            const geoRes = await fetch(
              `${getApiBase()}/api/autoshops/geocode?address=${encodeURIComponent(userZipCode)}`,
              {
                method: "GET",
                credentials: "include",
              }
            );
            
            if (geoRes.ok) {
              const geoData = await geoRes.json();
              if (geoData.lat && geoData.lng) {
                setLatitude(geoData.lat);
                setLongitude(geoData.lng);
                console.log(`✅ Auto-loaded coordinates from user's ZIP: ${geoData.lat}, ${geoData.lng}`);
              }
            }
          } catch (geoErr) {
            console.warn("⚠️ Could not auto-load shops from user's ZIP:", geoErr);
          }
        }
      } catch (err) {
        console.error("Failed to fetch user data", err);
      }
    };
    fetchUser();
  }, []);

  // Fetch available request types on mount
  useEffect(() => {
    const fetchAvailableTypes = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/service-request/available-types`, {
          credentials: "include",
        });
        const data = await res.json();
        
        if (data.availableTypes) {
          setAvailableRequestTypes(data.availableTypes);
          
          // SMART DEFAULTS: Auto-select if only 1 option available
          if (data.count === 1) {
            // Find the only enabled type
            const enabledType = Object.keys(data.availableTypes).find(
              key => data.availableTypes[key] === true
            );
            setRequestType(enabledType || 'direct');
          }
        }
        
        setRequestTypesLoaded(true);
      } catch (err) {
        console.error("Failed to fetch available request types", err);
        // Fallback: Only direct requests available
        setAvailableRequestTypes({ direct: true, announcement: false, bidding: false });
        setRequestType('direct');
        setRequestTypesLoaded(true);
      }
    };
    fetchAvailableTypes();
  }, []);

  // Fetch cars and services on mount
  useEffect(() => {
    const fetchCars = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/user/cars`, {
          credentials: "include",
        });
        const data = await res.json();

        if (res.ok) {
          setCars(data);

          const saved = JSON.parse(localStorage.getItem("pendingBooking"));

          if (data.length === 1) {
            setSelectedCarId(data[0].id);
          }

          if (saved?.selectedCarId) {
            setSelectedCarId(saved.selectedCarId);
          } else if (data.length > 0 && !selectedCarId) {
            setSelectedCarId(data[0].id);
          }
        }
      } catch (err) {
        console.error("Failed to load cars", err);
      }
    };

    const fetchServices = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/services/services`, {
          credentials: "include",
        });
        const data = await res.json();
        if (res.ok) setServices(data);
      } catch (err) {
        console.error("Failed to load services", err);
      }
    };

    fetchCars();
    fetchServices();

    const saved = localStorage.getItem("pendingBooking");
    if (saved) {
      const parsed = JSON.parse(saved);
      setSelectedCarId(parsed.selectedCarId || "");
      setSelectedServices(parsed.selectedServices || []);
      setSelectedAutoshop(parsed.selectedAutoshop || "");
      setAvailableDates(parsed.availableDates || []);
      setServiceDescription(parsed.serviceDescription || "");
    }
  }, []);

  // Fetch autoshops function - called when ZIP is submitted or coordinates change
  const fetchAutoshops = useCallback(async () => {
    if (longitude === null || latitude === null) {
      setAutoshops([]);
      setLoadingShops(false);
      return;
    }

    setLoadingShops(true);
    console.log(`🔍 Fetching shops for coordinates: ${latitude}, ${longitude}, radius: ${radius} miles`);
    
    try {
      const res = await fetch(
        `${getApiBase()}/api/autoshops/in-network?radius=${radius}&longitude=${longitude}&latitude=${latitude}`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: "Unknown error" }));
        console.error("❌ API error:", errorData);
        throw new Error(errorData.error || "Failed to fetch autoshops.");
      }
      
      const data = await res.json();
      console.log(`✅ Received ${Array.isArray(data) ? data.length : 0} shops`);
      
      if (Array.isArray(data) && data.length !== 0) {
      setAutoshops(data);
      } else {
        console.warn("⚠️ No shops found for this location and radius");
        setAutoshops([]); // Ensure it's still an array
      }
    } catch (err) {
      console.error("❌ Error loading autoshops:", err);
      setAutoshops([]);
    } finally {
      setLoadingShops(false);
    }
  }, [longitude, latitude, radius]);

  // Fetch autoshops when coordinates or radius change (but only if coordinates are set)
  useEffect(() => {
    // Only fetch if coordinates are explicitly set (from ZIP submission)
    if (longitude !== null && latitude !== null) {
      fetchAutoshops();
    }
  }, [fetchAutoshops]);

  // Handle form submission
  const submitServiceRequest = async () => {
    // Prevent duplicate submissions
    if (isSubmittingServiceRequest) {
      return;
    }

    // Validate required fields
    if (!selectedCarId) {
      alert("Please select a car.");
      return;
    }

    if (selectedServices.length === 0) {
      alert("Please select at least one service.");
      return;
    }

    if (!serviceDescription || serviceDescription.trim() === "") {
      alert("Please provide a description of the issue.");
      return;
    }

    if (availableDates.length === 0) {
      alert("Please select at least one available date.");
      return;
    }

    // If location is missing, prompt the user
    if (longitude === null || latitude === null) {
      try {
        const { lat, lng } = await handleRetryLocation();
        // Use the updated lat/lng for the request
        return submitServiceRequest(); // Recursively call with updated location
      } catch (err) {
        return; // Stop if location still not allowed
      }
    }

    setIsSubmittingServiceRequest(true);
    fetchAutoshops();

    const autoshopId = isNaN(Number(selectedAutoshop))
      ? null
      : Number(selectedAutoshop);

    try {
      const res = await fetch(
        `${getApiBase()}/api/serviceRequest/service-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            car_id: selectedCarId,
            selectedAutoshop: autoshopId || null,
            serviceIds: selectedServices,
            description: serviceDescription,
            availableDates,
            requestDate: requestDate,
            longitude: longitude,
            latitude: latitude,
            request_type: requestType,
            dropoff_week_start: dropoffWeekStart || null,
            dropoff_days: dropoffAvailableDays,
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to submit service request.");
      }

      alert("Service request submitted successfully!");
      setSelectedAutoshop("");
      setSelectedServices([]);
      setServiceDescription("");
      setAvailableDates([]);
      setDropoffWeekStart(null);
      setDropoffAvailableDays([]);
    } catch (err) {
      console.error("submitServiceRequest error:", err);
      alert("Error submitting service request.");
    } finally {
      setIsSubmittingServiceRequest(false);
    }
  };

  const handleRetryLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by your browser.");
        return reject("Geolocation not supported");
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude: lat, longitude: lng } = position.coords;
          setLatitude(lat);
          setLongitude(lng);
          resolve({ lat, lng });
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            alert(
              "Location access denied. Please enable it in browser settings."
            );
          } else {
            alert("Failed to get location. Try again.");
          }
          reject(error);
        }
      );
    });
  };

  const onZipSubmit = async (zipToUse) => {
    if (!zipToUse || zipToUse.length < 5) {
      alert("Please enter a valid 5-digit ZIP code.");
      return;
    }

    try {
      console.log(`🔍 Making fresh API call to Google for ZIP: ${zipToUse}`);
      
      // Use backend endpoint to get coordinates from ZIP (ensures fresh API call to Google)
      // This endpoint calls googleAPI.getLatLngFromZip which makes a fresh call to Google
      const res = await fetch(
        `${getApiBase()}/api/autoshops/geocode?address=${encodeURIComponent(zipToUse)}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: "Unknown error" }));
        throw new Error(errorData.message || "Failed to get location from ZIP code.");
      }

      const data = await res.json();
      
      if (data.lat && data.lng) {
        setLatitude(data.lat);
        setLongitude(data.lng);
        // Shops will automatically reload via useEffect when coordinates change
        console.log(`✅ Fresh Google API call successful - Updated location to ZIP ${zipToUse}: ${data.lat}, ${data.lng}`);
      } else {
        throw new Error("Invalid ZIP code response from server.");
      }
    } catch (err) {
      console.error("❌ Error fetching location from ZIP:", err);
      alert(`Error: ${err.message || "Failed to get location from ZIP code. Please try again."}`);
    }
  };

  return (
    <>
      <Header />
      <Box
        justifyContent="center"
        alignItems="center"
        display="flex"
        flexDirection="column"
        sx={{
          fontWeight: 600,
          color: "#000",
          margin: 3,
        }}
      >
        <Typography
          variant="h3"
          sx={{ my: 1, fontSize: { xs: "2rem", sm: "3rem" } }}
        >
          Book a Service
        </Typography>
        <Typography
          variant="h5"
          sx={{ textAlign: "center", fontSize: { xs: "1rem", sm: "1.5rem" } }}
        >
          Please fill out the form below to book a service for your vehicle.
        </Typography>
      </Box>
      <Box sx={{ maxWidth: 1000, margin: "0 auto", padding: 2 }}>
        <ServiceRequestForm
          cars={cars}
              selectedCarId={selectedCarId}
              setSelectedCarId={setSelectedCarId}
              selectedAutoshop={selectedAutoshop}
              setSelectedAutoshop={setSelectedAutoshop}
              radius={radius}
              newRadius={radius}
              setNewRadius={setRadius}
              onRadiusChange={(r) => setRadius(r)}
              autoshops={autoshops}
              selectedServices={selectedServices}
              setSelectedServices={setSelectedServices}
              services={services}
              serviceDescription={serviceDescription}
              setServiceDescription={setServiceDescription}
              availableDates={availableDates}
              setAvailableDates={setAvailableDates}
              submitServiceRequest={submitServiceRequest}
              isSubmittingServiceRequest={isSubmittingServiceRequest}
              userZip={userZip}
              setUserZip={setUserZip}
              zipCode={zipCode}
              onZipSubmit={onZipSubmit}
              latitude={latitude}
              longitude={longitude}
              requestType={requestType}
              setRequestType={setRequestType}
              availableRequestTypes={availableRequestTypes}
              requestTypesLoaded={requestTypesLoaded}
              dropoffWeekStart={dropoffWeekStart}
              setDropoffWeekStart={setDropoffWeekStart}
              dropoffAvailableDays={dropoffAvailableDays}
              setDropoffAvailableDays={setDropoffAvailableDays}
        />
      </Box>
      <Footer />
    </>
  );
};

export default BookService;
